import { json as codeMirrorJSON } from '@codemirror/lang-json';
import { ErrorMessage } from '@hookform/error-message';
import CodeMirror from '@uiw/react-codemirror';
import { cloneDeep, merge } from 'lodash';
import { type FC, useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { Hdd, Save as SaveIcon, XCircle } from 'react-bootstrap-icons';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useAuth, useNotifications, useRequest } from '../../../modules/hooks';
import type { Property } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { getApiErrorMessage } from '../../../modules/utils/transform';

interface PropertyConfigFormInputs {
  jsonConfig: string;
}

interface PropertyConfigModalProps {
  data?: Property;
  show: boolean;
  handleClose: () => void;
}

export const PropertyConfigModal: FC<PropertyConfigModalProps> = ({
  data: property,
  show,
  handleClose,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const [propertyConfig, setPropertyConfig] = useState<string>('');
  const [defaultConfig, setDefaultConfig] = useState<string>('');
  const [editableConfig, setEditableConfig] = useState<string>('');
  const [isValidJson, setIsValidJson] = useState<boolean>(true);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PropertyConfigFormInputs>({
    mode: 'onChange',
  });

  const { credentialsInfo } = useAuth()!;
  const getPropertyConfigUrl = `${endpoints.CONFIG}/details/${property?.uuid}`;
  const getDefaultConfigUrl = `${endpoints.CONFIG}/details/global-app-settings`;

  const [{ data: propertyConfigData, loading, error }, getConfigOfProperty] =
    useRequest<JSON>(getPropertyConfigUrl, 'get', {
      authToken: credentialsInfo?.token,
    });

  const [
    {
      data: defaultConfigDataResponse,
      loading: defaultConfigDataLoading,
      error: defaultConfigDataResponseError,
    },
    getDefaultConfig,
  ] = useRequest<JSON>(getDefaultConfigUrl, 'get', {
    authToken: credentialsInfo?.token,
  });

  const [{ error: savePropertyConfigError }, savePropertyConfig] = useRequest<JSON>(
    endpoints.CONFIG,
    'post',
    { authToken: credentialsInfo?.token },
    { manual: true },
  );

  const defaultConfigData = JSON.stringify({ PK: property?.uuid }, null, 2);

  const getModalTitle = () =>
    property ? `Settings of ${property.name}` : 'Settings of Property';

  const onSubmit: SubmitHandler<PropertyConfigFormInputs> = async () => {
    try {
      const parsedConfig = JSON.parse(propertyConfig);
      ['updatedAt', 'createdAt'].forEach((field) => delete parsedConfig[field]); // Remove timestamps fields
      await savePropertyConfig({ data: parsedConfig });
      setSimpleToasts({
        type: 'success',
        message: 'Settings saved successfully.',
        show: true,
      });
      reset();
      handleClose();
    } catch (err) {
      setSimpleToasts({
        type: 'danger',
        message: 'An error occurred while saving the settings.',
        show: true,
      });
    }
  };

  const handleEditorChange = (value: string) => {
    setValue('jsonConfig', value, { shouldValidate: true });
    setPropertyConfig(value);
  };

  const handleLoadDefaultConfig = () => {
    if (!defaultConfig) {
      setSimpleToasts({
        type: 'danger',
        message: defaultConfigDataResponseError
          ? getApiErrorMessage(defaultConfigDataResponseError)
          : 'Default configuration is not available.',
        show: true,
      });

      return;
    }

    if (!propertyConfig) {
      setPropertyConfig(defaultConfig);
      setEditableConfig(defaultConfig);
      setValue('jsonConfig', defaultConfig, { shouldValidate: true });
      setIsValidJson(true);
      return;
    }

    try {
      const parsedCurrentConfig = JSON.parse(propertyConfig);
      const parsedDefaultConfig = JSON.parse(defaultConfig);
      const mergedConfig = merge(cloneDeep(parsedDefaultConfig), parsedCurrentConfig);
      const mergedConfigString = JSON.stringify(mergedConfig, null, 2);
      setPropertyConfig(mergedConfigString);
      setEditableConfig(mergedConfigString);
      setValue('jsonConfig', mergedConfigString, { shouldValidate: true });
      setIsValidJson(true);
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to merge configurations. Please check the JSON formats.',
        show: true,
      });
    }
  };

  const fetchConfigOfProperty = useCallback(() => {
    if (property) {
      getConfigOfProperty();
    } else {
      setPropertyConfig(defaultConfigData);
    }
  }, [property, getConfigOfProperty, defaultConfigData]);

  useEffect(() => {
    if (show) {
      fetchConfigOfProperty();
    }
  }, [show, fetchConfigOfProperty]);

  useEffect(() => {
    if (error) {
      const message = getApiErrorMessage(error);
      setSimpleToasts({ type: 'danger', message, show: true });
      setPropertyConfig(defaultConfigData);
      setEditableConfig(defaultConfigData);
    } else if (propertyConfigData?.result) {
      setPropertyConfig(JSON.stringify(propertyConfigData.result, null, 2));
      setEditableConfig(JSON.stringify(propertyConfigData.result, null, 2));
    } else {
      setPropertyConfig(defaultConfigData);
      setEditableConfig(defaultConfigData);
    }
  }, [propertyConfigData, error, defaultConfigData, setSimpleToasts]);

  useEffect(() => {
    if (defaultConfigDataResponse?.result) {
      const updatedConfig = {
        ...defaultConfigDataResponse.result,
      };
      setDefaultConfig(JSON.stringify(updatedConfig, null, 2));
    }
  }, [defaultConfigDataResponse, property?.uuid]);

  useEffect(() => {
    if (savePropertyConfigError) {
      const message = getApiErrorMessage(savePropertyConfigError);
      setSimpleToasts({ type: 'danger', message, show: true });
    }
  }, [savePropertyConfigError, setSimpleToasts]);

  return (
    <Modal
      show={show}
      onHide={() => {
        reset();
        handleClose();
      }}
      size="xl"
      backdrop="static"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header>
          <Modal.Title className="h5">{getModalTitle()}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {loading || defaultConfigDataLoading ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Form.Group controlId="jsonConfig">
              <Form.Label>Environment Settings (JSON)</Form.Label>
              <CodeMirror
                value={editableConfig}
                extensions={[codeMirrorJSON()]}
                onChange={handleEditorChange}
                theme="light"
              />
              <input
                type="hidden"
                value={editableConfig}
                {...register('jsonConfig', {
                  required: 'JSON configuration is required.',
                  validate: (value) => {
                    try {
                      JSON.parse(value);
                      setIsValidJson(true);
                      return true;
                    } catch {
                      setIsValidJson(false);
                      return 'Invalid JSON format.';
                    }
                  },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="jsonConfig"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="warning" onClick={handleLoadDefaultConfig} className="btn-sm">
            <Hdd className="dropdown-icon me-1" />
            Load default settings
          </Button>
          <div>
            <Button variant="secondary" onClick={handleClose} className="btn-sm">
              <XCircle className="dropdown-icon me-1" />
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="ms-2 btn-sm"
              disabled={!isValidJson}
            >
              <SaveIcon className="dropdown-icon me-1" />
              Save
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
